/**
 * The tidy tree: the first real layout.
 *
 * A mind map grows sideways from a root, and the problem it poses is the one Reingold and
 * Tilford named — arrange a tree so that no two subtrees overlap, parents sit centred over their
 * children, and identical subtrees look identical wherever they appear.
 *
 * The method here is the layered form of that algorithm. Every node reserves a *band* across the
 * axis it does not grow along, sized to hold everything beneath it; siblings stack their bands
 * in order; a parent centres itself on the band its children fill. It runs in one post-order pass
 * to measure and one pre-order pass to place, so a 10,000-node map costs two walks and no search.
 *
 * What it deliberately does not do is Reingold-Tilford's *contour tightening* — the refinement
 * that lets a deep-left subtree slide under a shallow-right one. For text in boxes, which is what
 * a mind map is, interlocking subtrees read as a jumble: the eye follows a branch by the band it
 * occupies, and taking that away to save whitespace trades legibility for density. If a map ever
 * needs the density, the contours go in `bandOf` and nothing else changes.
 *
 * Three rules the shape of a mind map imposes, none of them in the original algorithm:
 *
 * - **Two sides.** A left-right tree grows both ways from the root, so the pack runs twice, once
 *   per side, and the root belongs to neither.
 * - **Collapsed branches take no space.** A folded subtree is not laid out at all; folding a
 *   branch should close the gap it left, not leave a hole.
 * - **Pinned nodes opt out.** A node the user dragged keeps its position and leaves the stack —
 *   reserving a band for a node that is somewhere else entirely would be a hole in the middle of
 *   the map with nothing in it.
 */
import { childrenOf, pathToRoot } from '@orbit-mind/domain';
import { parseLayoutId } from '@orbit-mind/shared-types';
import { onlyMoves } from './moves';
import { spacingFrom, } from './types';
/** Along the axis the tree grows, is the direction positive? */
function isForward(growth) {
    return growth === 'right' || growth === 'down';
}
/** Does this tree grow horizontally? Decides which extent is "depth" and which is "band". */
function isHorizontal(growth) {
    return growth === 'right' || growth === 'left';
}
/**
 * Children that take part in the pack.
 *
 * A collapsed node has none, whatever the document says: its subtree is not drawn, so reserving
 * space for it would leave a hole where a branch used to be.
 */
function packedChildren(pack, node) {
    if (node.collapsed)
        return [];
    return childrenOf(pack.document, node.id).filter((child) => !child.hidden);
}
/** A node the user placed by hand, which the layout leaves alone. */
function isPinned(pack, node) {
    return node.pinned === true && pack.preserveManualPositions;
}
/**
 * How much room a subtree needs across the growth axis.
 *
 * A leaf needs its own extent. A branch needs the sum of its children's bands plus the gaps
 * between them — and at least its own extent, or a node wider than everything under it would
 * overlap its neighbours.
 */
function bandOf(pack, node, growth) {
    const key = `${node.id}|${growth}`;
    const cached = pack.bands.get(key);
    if (cached !== undefined)
        return cached;
    const own = across(pack.extentOf(node.id), growth);
    const children = packedChildren(pack, node).filter((child) => !isPinned(pack, child));
    let band = own;
    if (children.length > 0) {
        // Through `spanAcross`, not `bandOf`: a child that carries a family of its own occupies a
        // different side of its rectangle, and summing the wrong side is how two branches end up
        // drawn on top of each other.
        const total = children.reduce((sum, child) => sum + spanAcross(pack, child, growth), 0);
        band = Math.max(own, total + pack.spacing.sibling * (children.length - 1));
    }
    pack.bands.set(key, band);
    return band;
}
/**
 * The growth a node imposes on everything under it, or `null` when it has no opinion.
 *
 * A node carries a `layoutId` only when somebody set one on that branch. Free-form is not an
 * opinion about direction — there is nothing for a tree to do with it — so it answers `null` too
 * and the branch keeps growing the way its parent does.
 */
function ownGrowth(pack, node) {
    if (node.layoutId === undefined)
        return null;
    const sides = growthFor(node.layoutId);
    if (sides === null)
        return null;
    return branchGrowth(pack.document, node, sides);
}
/** Two directions are on the same axis when both are horizontal or both are vertical. */
function sameAxis(a, b) {
    return isHorizontal(a) === isHorizontal(b);
}
/**
 * How much room a subtree needs *across* the axis its parent grows along.
 *
 * This is the measurement that changed when a branch was allowed a family of its own, and it is
 * the whole reason a branch running downwards inside a left-right map does not land on top of its
 * neighbour. A subtree occupies a rectangle; which side of that rectangle faces the parent
 * depends on whether the two families grow along the same axis.
 *
 * - **Same axis** — the subtree stacks the way the parent expects, and its band is what it always
 *   was: the sum of its children's bands.
 * - **Perpendicular** — the subtree runs *away* across the parent's band, so what the parent has
 *   to reserve is how far it reaches: its span along its own growth.
 *
 * Getting this wrong does not look like a rounding error. It looks like two branches drawn on top
 * of each other.
 */
function spanAcross(pack, node, parentGrowth) {
    const own = ownGrowth(pack, node);
    if (own !== null && !sameAxis(own, parentGrowth))
        return spanAlong(pack, node, own);
    return bandOf(pack, node, own ?? parentGrowth);
}
/**
 * How far a subtree reaches along the axis it grows on.
 *
 * Its own extent, plus the rank gap, plus the deepest of its children — where a child that grows
 * perpendicular contributes its *band* instead, for the same reason and by the same swap as
 * above. Memoised per node and direction: the pre-order placement pass asks for the same numbers
 * the post-order measuring pass just worked out.
 */
function spanAlong(pack, node, growth) {
    const key = `${node.id}|${growth}`;
    const cached = pack.spans.get(key);
    if (cached !== undefined)
        return cached;
    const own = along(pack.extentOf(node.id), growth);
    const children = packedChildren(pack, node).filter((child) => !isPinned(pack, child));
    let span = own;
    if (children.length > 0) {
        const deepest = children.reduce((most, child) => {
            const childGrowth = ownGrowth(pack, child);
            const reach = childGrowth !== null && !sameAxis(childGrowth, growth)
                ? bandOf(pack, child, childGrowth)
                : spanAlong(pack, child, childGrowth ?? growth);
            return Math.max(most, reach);
        }, 0);
        span = own + pack.spacing.rank + deepest;
    }
    pack.spans.set(key, span);
    return span;
}
function across(extent, growth) {
    return isHorizontal(growth) ? extent.height : extent.width;
}
function along(extent, growth) {
    return isHorizontal(growth) ? extent.width : extent.height;
}
/**
 * Places a subtree.
 *
 * `depth` is the coordinate along the growth axis for this node's leading edge; `bandStart` is
 * where its band begins across it. Both are absolute, so a subtree can be packed from wherever
 * its pinned ancestor happens to sit.
 */
function placeSubtree(pack, node, growth, depth, bandStart) {
    const extent = pack.extentOf(node.id);
    const own = ownGrowth(pack, node);
    const perpendicular = own !== null && !sameAxis(own, growth);
    const band = spanAcross(pack, node, growth);
    /*
     * Where the node sits inside the band its subtree reserved.
     *
     * Centred, normally: that is what makes a parent sit level with the middle of its children
     * rather than level with the first of them.
     *
     * A branch that grows *perpendicular* to its parent is the exception, and getting it wrong is
     * not subtle. Its subtree runs away from the node along the parent's band, so the band measures
     * the subtree's whole reach — and centring the node in it puts the node halfway down and pushes
     * everything below it out the far end and onto the next branch. The node goes at the edge the
     * subtree grows away from: the start for a forward direction, the end for a backward one.
     */
    const centred = perpendicular
        ? isForward(own)
            ? bandStart
            : bandStart + band - across(extent, growth)
        : bandStart + (band - across(extent, growth)) / 2;
    const pinned = isPinned(pack, node);
    const position = pinned
        ? { x: node.x, y: node.y }
        : toPoint(growth, depth, centred, along(extent, growth));
    if (!pinned)
        pack.placements[node.id] = position;
    const children = packedChildren(pack, node);
    if (children.length === 0)
        return;
    /*
     * From here down, the node's own family decides the direction.
     *
     * The node itself was placed by its parent's growth — it is attached to the parent, and a
     * branch that moved when it was given a family would tear itself off the map. Everything
     * *under* it grows the way this branch says, which is what "the layout of this branch" means.
     */
    const childGrowth = ownGrowth(pack, node) ?? growth;
    // Children hang off the far edge of this node — from where it *is*, when it is pinned, so a
    // dragged branch takes its subtree with it.
    const nextDepth = isForward(childGrowth)
        ? alongOf(childGrowth, position) + along(extent, childGrowth) + pack.spacing.rank
        : alongOf(childGrowth, position) - pack.spacing.rank;
    // Pinned children are placed by their own coordinates and take no room in the stack.
    const packed = children.filter((child) => !isPinned(pack, child));
    const totalBand = packed.reduce((sum, child) => sum + spanAcross(pack, child, childGrowth), 0) +
        pack.spacing.sibling * Math.max(0, packed.length - 1);
    /*
     * Where the run of children starts.
     *
     * Centred on the node, normally — that is what makes a parent sit level with the middle of its
     * children rather than level with the first of them.
     *
     * A branch that turned perpendicular is the exception again, and for a reason the outer packing
     * cannot fix: its children run across the axis its *parent* grows along, and a centred run
     * therefore extends backwards — into the column its own ancestors occupy, which nothing has
     * reserved because nothing in a one-directional tree ever grows backwards. Starting the run at
     * the node's leading edge keeps the whole subtree in front of the node it hangs from.
     */
    let cursor = perpendicular
        ? acrossOf(childGrowth, position)
        : acrossOf(childGrowth, position) + across(extent, childGrowth) / 2 - totalBand / 2;
    for (const child of children) {
        placeSubtree(pack, child, childGrowth, nextDepth, cursor);
        if (!isPinned(pack, child)) {
            cursor += spanAcross(pack, child, childGrowth) + pack.spacing.sibling;
        }
    }
}
/**
 * Builds a point from the two axes.
 *
 * A leftward tree is measured from its *trailing* edge, because the node's `x` is still its left
 * side — growing left means the box ends where the depth says it starts.
 */
function toPoint(growth, depth, bandPosition, extentAlong) {
    if (isHorizontal(growth)) {
        return { x: growth === 'right' ? depth : depth - extentAlong, y: bandPosition };
    }
    return { x: bandPosition, y: growth === 'down' ? depth : depth - extentAlong };
}
function alongOf(growth, point) {
    return isHorizontal(growth) ? point.x : point.y;
}
function acrossOf(growth, point) {
    return isHorizontal(growth) ? point.y : point.x;
}
/**
 * Which way a branch of the root goes.
 *
 * An explicit side is honoured — that is what the side-change command writes, and overriding it
 * would make the command look broken. `auto` is balanced by alternating, so a map with six
 * branches opens three each way instead of hanging all six off one side.
 *
 * Alternating by *index among the auto branches* rather than at random keeps it stable: adding a
 * seventh branch decides where the seventh goes and leaves the other six where they were.
 */
function sideOfBranch(node, autoIndex, sides) {
    if (node.side === 'left' || node.side === 'up')
        return sides[0];
    if (node.side === 'right' || node.side === 'down')
        return sides[1];
    return autoIndex % 2 === 0 ? sides[1] : sides[0];
}
function growthFor(layoutId) {
    const { direction } = parseLayoutId(layoutId);
    switch (direction) {
        case 'left-right':
            return ['left', 'right'];
        case 'right':
            return ['right', 'right'];
        case 'left':
            return ['left', 'left'];
        case 'down':
            return ['down', 'down'];
        case 'up':
            return ['up', 'up'];
        case 'up-down':
            return ['up', 'down'];
        // `free-form` parses to no direction, and there is nothing for a tree to do with it.
        case null:
            return null;
    }
}
/**
 * Arranges a document as a tidy tree.
 *
 * The root does not move. Everything is measured from where it already is, so running a layout
 * does not scroll the map out from under whoever asked for it.
 */
export function packTree(input, layoutId) {
    const sides = growthFor(layoutId);
    if (sides === null)
        return { positions: {} };
    const root = input.document.nodes[input.document.map.rootNodeId];
    if (root === undefined)
        return { positions: {} };
    const pack = {
        document: input.document,
        extentOf: input.extentOf,
        spacing: spacingFrom(input.spacing),
        preserveManualPositions: input.preserveManualPositions,
        placements: {},
        bands: new Map(),
        spans: new Map(),
    };
    const rootExtent = input.extentOf(root.id);
    const branches = packedChildren(pack, root);
    let autoIndex = 0;
    const byGrowth = new Map();
    for (const branch of branches) {
        const growth = sideOfBranch(branch, branch.side === 'auto' ? autoIndex : -1, sides);
        if (branch.side === 'auto')
            autoIndex += 1;
        const bucket = byGrowth.get(growth) ?? [];
        bucket.push(branch);
        byGrowth.set(growth, bucket);
    }
    for (const [growth, bucket] of byGrowth) {
        const packed = bucket.filter((branch) => !isPinned(pack, branch));
        const totalBand = packed.reduce((sum, branch) => sum + spanAcross(pack, branch, growth), 0) +
            pack.spacing.sibling * Math.max(0, packed.length - 1);
        const depth = isForward(growth)
            ? alongOf(growth, root) + along(rootExtent, growth) + pack.spacing.rank
            : alongOf(growth, root) - pack.spacing.rank;
        let cursor = acrossOf(growth, root) + across(rootExtent, growth) / 2 - totalBand / 2;
        for (const branch of bucket) {
            placeSubtree(pack, branch, growth, depth, cursor);
            if (!isPinned(pack, branch)) {
                cursor += spanAcross(pack, branch, growth) + pack.spacing.sibling;
            }
        }
    }
    return { positions: onlyMoves(input.document, pack.placements) };
}
/**
 * One branch, arranged on its own.
 *
 * A map can be a curved tree with one branch running downwards, which is what "the layout of this
 * branch" means. The whole-map packing spreads the root's children across the sides the family
 * offers; a branch does not get that choice — it already hangs from a side, and growing it the
 * other way would send it back through the map it belongs to.
 *
 * So the growth is decided once, from the family and from the side the branch inherits, and the
 * subtree is packed along it. The scope node itself does not move: it is where the branch is
 * attached, and moving it would drag the arrangement of everything above it.
 */
export function packBranch(input, layoutId, scopeNodeId) {
    const sides = growthFor(layoutId);
    if (sides === null)
        return { positions: {} };
    const scope = input.document.nodes[scopeNodeId];
    if (scope === undefined)
        return { positions: {} };
    const pack = {
        document: input.document,
        extentOf: input.extentOf,
        spacing: spacingFrom(input.spacing),
        preserveManualPositions: input.preserveManualPositions,
        placements: {},
        bands: new Map(),
        spans: new Map(),
    };
    const growth = branchGrowth(input.document, scope, sides);
    const extent = input.extentOf(scope.id);
    const children = packedChildren(pack, scope);
    const packed = children.filter((child) => !isPinned(pack, child));
    const totalBand = packed.reduce((sum, child) => sum + spanAcross(pack, child, growth), 0) +
        pack.spacing.sibling * Math.max(0, packed.length - 1);
    const depth = isForward(growth)
        ? alongOf(growth, scope) + along(extent, growth) + pack.spacing.rank
        : alongOf(growth, scope) - pack.spacing.rank;
    let cursor = acrossOf(growth, scope) + across(extent, growth) / 2 - totalBand / 2;
    for (const child of children) {
        placeSubtree(pack, child, growth, depth, cursor);
        if (!isPinned(pack, child)) {
            cursor += spanAcross(pack, child, growth) + pack.spacing.sibling;
        }
    }
    return { positions: onlyMoves(input.document, pack.placements) };
}
/**
 * Which way a branch grows.
 *
 * A family that offers two directions gives a branch the one its own side points to — and a
 * branch's side is inherited, so the answer comes from the child of the root it hangs under
 * rather than from the node itself. A family that offers one direction gives that one to
 * everybody, which is what makes "this branch runs downwards" mean the same thing wherever the
 * branch is.
 */
function branchGrowth(document, scope, sides) {
    const [first, second] = sides;
    if (first === second)
        return first;
    // The ancestor that is a child of the root is the one that chose a side; everything below it
    // inherits. `pathToRoot` is ordered from the node upwards, so the last entry before the root is
    // that ancestor — and for a branch of the root, it is the node itself.
    const path = pathToRoot(document, scope.id);
    const branch = path.find((node) => node.parentId === document.map.rootNodeId) ?? scope;
    return branch.side === 'left' ? first : second;
}
/** A tree layout for one id. The families differ in how they *draw*, not in how they pack. */
export function treeLayout(layoutId, name) {
    return { id: layoutId, name, run: (input) => packTree(input, layoutId) };
}
export const CURVED_TREE_LR_ID = 'curved-line-tree/left-right';
export const DIRECT_TREE_LR_ID = 'direct-line-tree/left-right';
export const SIMPLE_TREE_DOWN_ID = 'simple-tree-direct/down';
/**
 * The three tree layouts the MVP ships.
 *
 * All the same packing, and that is the point rather than an economy. A family decides how its
 * hierarchy links are *drawn* — `routingForLayout` in the scene reads the id and answers curved,
 * straight or orthogonal — and where the boxes go is a separate question with the same answer.
 * Two packings that agreed today would disagree the first time one of them was improved.
 *
 * `simple-tree-direct/down` differs only in its direction, which `growthFor` reads off the id:
 * the same code that stacks bands vertically for a left-right tree stacks them horizontally for
 * a downward one, because "across the axis the tree grows" is the only thing it ever needed.
 */
export const curvedTreeLeftRight = treeLayout(CURVED_TREE_LR_ID, 'Curved tree, left and right');
export const directTreeLeftRight = treeLayout(DIRECT_TREE_LR_ID, 'Direct tree, left and right');
export const simpleTreeDown = treeLayout(SIMPLE_TREE_DOWN_ID, 'Simple tree, downwards');
export const ORG_CHART_ID = 'cornered-tree/down';
/**
 * The organisation chart: a downward tree, drawn with square corners.
 *
 * The same packing as the others, which is the right amount of new code for it — what makes an
 * org chart read as one is the corners, and the renderer draws `cornered` families that way.
 */
export const orgChart = treeLayout(ORG_CHART_ID, 'Org chart');
//# sourceMappingURL=tree.js.map